import type { InspectionTemplate, TemplateSection } from 'inspection-types';

export function cloneTemplateSection(section: TemplateSection): TemplateSection {
  return {
    ...section,
    items: section.items.map(item => ({ ...item })),
  };
}

export function mergeRemovedSectionSnapshotsIntoOrderedSections(
  serverSections: TemplateSection[],
  removedSectionSnapshots: Record<string, TemplateSection>
): TemplateSection[] {
  const serverIds = new Set(serverSections.map(s => s.id));
  const extras = Object.values(removedSectionSnapshots).filter(
    s => !serverIds.has(s.id)
  );
  return [...serverSections, ...extras].sort((a, b) => a.order - b.order);
}

export function countSectionsAndQuestions(template: InspectionTemplate): {
  sections: number;
  questions: number;
} {
  const activeSections = template.sections.filter(
    section => section.removed !== true
  );
  const questions = activeSections.reduce(
    (total, section) =>
      total + section.items.filter(item => item.removed !== true).length,
    0
  );

  return { sections: activeSections.length, questions };
}

/** Section title for accordion and sidebar (template section name only). */
export function formatSectionTitle(name: string): string {
  return name;
}

/**
 * Client-side projection after dropping an item and bumping aggregate version.
 * For optimistic UI where the mutation implies the layout; callers that need authoritative
 * data should refetch (e.g. `currentInspectionTemplate`).
 */
export function deriveInspectionTemplateAfterItemRemoved(
  current: InspectionTemplate,
  itemId: string,
  newAggregateVersion: number
): InspectionTemplate {
  return {
    ...current,
    version: newAggregateVersion,
    sections: current.sections.map(section => ({
      ...section,
      items: section.items.filter(item => item.id !== itemId),
    })),
  };
}

export function deriveInspectionTemplateAfterSectionRemoved(
  current: InspectionTemplate,
  sectionId: string,
  newAggregateVersion: number
): InspectionTemplate {
  return {
    ...current,
    version: newAggregateVersion,
    sections: current.sections.filter(section => section.id !== sectionId),
  };
}
